/** Local only developer preview for self verification outcome modals.
    Reused by NPR and Non-PCN forms to preview each possible result without completing the full form flow.
 **/

import { useId, useState } from 'react';
import { Button } from '@trussworks/react-uswds';
import EnvironmentVisibility from '@components/environment-visibility/EnvironmentVisibility';
import SelfVerifyResultModal from '@forms/components/modals/self-verify-result/SelfVerifyResultModal';
import { useModal } from '@stores';
import { NprResultTypes, PcnResultTypes } from '@src/utils/enums';

type SelfVerifyResultType = PcnResultTypes | NprResultTypes;

type SelfVerifyOutcomePreviewProps<T extends SelfVerifyResultType> = {
  outcomes: readonly T[];
  defaultResult: T;
  label: string;
};

const SelfVerifyOutcomePreview = <T extends SelfVerifyResultType>({
  outcomes,
  defaultResult,
  label,
}: SelfVerifyOutcomePreviewProps<T>) => {
  const [result, setResult] = useState<T>(defaultResult);
  const selectId = useId();
  const openModal = useModal((state) => state.openModal);

  return (
    <EnvironmentVisibility mode='show' local>
      <div className='display-flex flex-align-end gap-2 margin-bottom-2'>
        <div>
          <label className='usa-label margin-top-0' htmlFor={selectId}>
            {label}
          </label>

          <select
            id={selectId}
            className='usa-select'
            value={result}
            onChange={(event) => setResult(event.target.value as T)}
          >
            {outcomes.map((outcome) => (
              <option key={outcome} value={outcome}>
                {outcome}
              </option>
            ))}
          </select>
        </div>

        <Button className='margin-left-1' type='button' onClick={() => openModal(SelfVerifyResultModal, { result })}>
          Preview Modal
        </Button>
      </div>
    </EnvironmentVisibility>
  );
};

export default SelfVerifyOutcomePreview;
